import { ArrowRight, Download, Eye, SearchX } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Button from "@/components/ui/Button";
import DataTable, { type Column } from "@/components/ui/DataTable";
import EmptyState from "@/components/ui/EmptyState";
import StatusBadge from "@/components/ui/StatusBadge";
import { ToastProvider, useToast } from "@/components/ui/Toast";
import { adminService } from "@/services/apiServices";
import type { LogEntry } from "@/mocks/logs";
import { formatDate, formatTime } from "@/pages/admin/data";
import LogsFilterBar from "./components/FilterBar";
import PayloadModal from "./components/PayloadModal";

function inRange(timestamp: string, range: string, customFrom: string, customTo: string): boolean {
  if (!timestamp) return true;
  const d = new Date(timestamp);
  if (isNaN(d.getTime())) return true;
  const now = new Date();

  switch (range) {
    case "Today":
      return d.toDateString() === now.toDateString();
    case "Last 7 Days":
      return d.getTime() >= now.getTime() - 7 * 86400000;
    case "Last 30 Days":
      return d.getTime() >= now.getTime() - 30 * 86400000;
    case "This Month":
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    case "Last 3 Months":
      return d.getTime() >= now.getTime() - 90 * 86400000;
    case "Custom Range": {
      if (customFrom) {
        const f = new Date(`${customFrom}T00:00:00`);
        if (!isNaN(f.getTime()) && d.getTime() < f.getTime()) return false;
      }
      if (customTo) {
        const t = new Date(`${customTo}T23:59:59.999`);
        if (!isNaN(t.getTime()) && d.getTime() > t.getTime()) return false;
      }
      return true;
    }
    case "All Time":
    default:
      return true;
  }
}

function downloadCsv(rows: (string | number | null | undefined)[][], filename: string) {
  const content = rows
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? "");
          if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
            return `"${s.replace(/"/g, '""')}"`;
          }
          return s;
        })
        .join(",")
    )
    .join("\r\n");

  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AdminLogs() {
  return (
    <ToastProvider>
      <LogsInner />
    </ToastProvider>
  );
}

function LogsInner() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [apiRetailers, setApiRetailers] = useState<{ id: string; shopName: string; name: string }[]>([]);
  const [search, setSearch] = useState("");
  const [retailer, setRetailer] = useState("");
  const [eventType, setEventType] = useState("");
  const [source, setSource] = useState("");
  const [range, setRange] = useState("All Time");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);

  useEffect(() => {
    const retailerParam = searchParams.get("retailer") || searchParams.get("search");
    if (retailerParam) {
      setSearch(retailerParam);
    }
  }, [searchParams]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    Promise.all([
      adminService.getLogs(),
      adminService.getRetailers({ limit: 100 }),
    ])
      .then(([logsRes, retailersRes]: any[]) => {
        if (!isMounted) return;
        setLogs(logsRes?.data?.logs ?? []);
        setApiRetailers(retailersRes?.data?.retailers ?? []);
      })
      .catch((err: any) => {
        console.warn("Failed to fetch backend audit logs or retailers:", err);
        if (isMounted) {
          setLogs([]);
          setApiRetailers([]);
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const retailerOptions = useMemo(() => {
    const map = new Map<string, string>();
    apiRetailers.forEach((r) => {
      map.set(r.id, r.shopName ? `${r.shopName} (${r.id})` : `${r.name} (${r.id})`);
    });
    logs.forEach((e) => {
      if (e.retailerId && !map.has(e.retailerId)) {
        map.set(e.retailerId, e.shopName ? `${e.shopName} (${e.retailerId})` : e.retailerId);
      }
    });
    return Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [apiRetailers, logs]);

  const filtered = useMemo(() => {
    let list = logs.slice();
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((e) =>
        `${e.refNo || ""} ${e.shopName || ""} ${e.retailerId || ""} ${e.event || ""} ${e.description || ""}`
          .toLowerCase()
          .includes(q)
      );
    }
    if (retailer) list = list.filter((e) => e.retailerId === retailer);
    if (eventType) list = list.filter((e) => e.event === eventType);
    if (source) list = list.filter((e) => e.source === source);
    list = list.filter((e) => inRange(e.timestamp, range, customFrom, customTo));
    return list;
  }, [logs, search, retailer, eventType, source, range, customFrom, customTo]);

  const chips = useMemo(() => {
    const c: { label: string; onRemove: () => void }[] = [];
    if (search.trim()) c.push({ label: `Search: ${search}`, onRemove: () => setSearch("") });
    if (retailer) {
      const match = retailerOptions.find((o) => o.value === retailer);
      c.push({ label: `Retailer: ${match?.label ?? retailer}`, onRemove: () => setRetailer("") });
    }
    if (eventType) c.push({ label: `Event: ${eventType}`, onRemove: () => setEventType("") });
    if (source) c.push({ label: `Source: ${source}`, onRemove: () => setSource("") });
    if (range !== "All Time") c.push({ label: `Range: ${range}`, onRemove: () => setRange("All Time") });
    if (range === "Custom Range" && customFrom) c.push({ label: `From: ${customFrom}`, onRemove: () => setCustomFrom("") });
    if (range === "Custom Range" && customTo) c.push({ label: `To: ${customTo}`, onRemove: () => setCustomTo("") });
    return c;
  }, [logs, search, retailer, retailerOptions, eventType, source, range, customFrom, customTo]);

  function resetFilters() {
    setSearch("");
    setRetailer("");
    setEventType("");
    setSource("");
    setRange("All Time");
    setCustomFrom("");
    setCustomTo("");
  }

  function handleExport() {
    if (filtered.length === 0) {
      toast.error("No logs to export matching current filters");
      return;
    }
    const headers = [
      "Timestamp",
      "Date",
      "Time",
      "Reference No",
      "Retailer ID",
      "Retailer / Shop Name",
      "Event",
      "Description",
      "Old Status",
      "New Status",
      "Source",
      "Performed By",
    ];
    const rows = filtered.map((e) => [
      e.timestamp,
      formatDate(e.timestamp),
      formatTime(e.timestamp),
      e.refNo || "",
      e.retailerId || "",
      e.shopName || "",
      e.event || "",
      e.description || "",
      e.oldStatus || "",
      e.newStatus || "",
      e.source || "",
      e.performedBy || "",
    ]);
    downloadCsv([headers, ...rows], `audit-logs-${Date.now()}.csv`);
    toast.success(`Exported ${filtered.length} log ${filtered.length === 1 ? "entry" : "entries"}`);
  }

  const columns: Column<LogEntry>[] = [
    {
      key: "timestamp",
      header: "Timestamp",
      sortable: true,
      render: (row) => (
        <div>
          <p className="text-[13px] text-ink">{formatDate(row.timestamp)}</p>
          <p className="text-xs text-ink-secondary">{formatTime(row.timestamp)}</p>
        </div>
      ),
    },
    {
      key: "refNo",
      header: "Ref No",
      render: (row) => {
        const displayRef = row.refNo || row.retailerId || "—";
        return (
          <button
            type="button"
            onClick={() => {
              if (row.refNo && (row.refNo.startsWith("EM") || row.refNo.startsWith("REF-"))) {
                navigate(`/admin/mandates/${row.refNo}`);
              } else if (row.retailerId) {
                navigate(`/admin/retailers/${row.retailerId}`);
              }
            }}
            className="text-[13px] font-medium text-primary-500 hover:text-primary-600 cursor-pointer"
          >
            {displayRef}
          </button>
        );
      },
    },
    {
      key: "shopName",
      header: "Retailer",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.shopName}</p>
          <p className="text-xs text-ink-secondary">{row.retailerId}</p>
        </div>
      ),
    },
    {
      key: "event",
      header: "Event",
      render: (row) => (
        <div>
          <p className="text-sm font-medium text-ink">{row.event}</p>
          <p className="text-xs text-ink-secondary">{row.description}</p>
        </div>
      ),
    },
    {
      key: "statusChange",
      header: "Status Change",
      render: (row) =>
        row.oldStatus && row.newStatus ? (
          <span className="inline-flex items-center gap-2">
            <StatusBadge status={row.oldStatus} size="sm" />
            <ArrowRight className="h-3.5 w-3.5 text-ink-muted" />
            <StatusBadge status={row.newStatus} size="sm" />
          </span>
        ) : (
          <span className="text-[13px] text-ink-muted">—</span>
        ),
    },
    {
      key: "source",
      header: "Source",
      render: (row) => (
        <span className="inline-flex whitespace-nowrap rounded-full bg-[#F1F5F9] px-2.5 py-0.5 text-xs text-[#475569]">
          {row.source}
        </span>
      ),
    },
  ];

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-ink">Activity Logs</h1>
          <p className="mt-1 text-sm text-ink-secondary">
            Complete audit trail of every status change and system event
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={handleExport}
          leftIcon={<Download className="h-4 w-4" />}
        >
          Export Logs
        </Button>
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <LogsFilterBar
          search={search}
          onSearch={setSearch}
          range={range}
          onRange={setRange}
          customFrom={customFrom}
          customTo={customTo}
          onCustomFrom={setCustomFrom}
          onCustomTo={setCustomTo}
          retailer={retailer}
          onRetailer={setRetailer}
          retailerOptions={retailerOptions}
          eventType={eventType}
          onEventType={setEventType}
          source={source}
          onSource={setSource}
          onReset={resetFilters}
          chips={chips}
        />

        {loading ? (
          <DataTable columns={columns} data={[]} rowKey={(r) => r.id} loading />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={SearchX}
            title="No logs found"
            description="Try adjusting your filters or search term."
            actionLabel="Reset Filters"
            onAction={resetFilters}
          />
        ) : (
          <DataTable
            columns={columns}
            data={filtered}
            rowKey={(r) => r.id}
            pageSize={50}
            pageSizeOptions={[50, 100, 200]}
            actions={(row) => (
              <button
                type="button"
                onClick={() => setSelectedEntry(row)}
                className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink hover:bg-slate-50 cursor-pointer"
              >
                <Eye className="h-4 w-4 text-ink-muted" />
                View Details
              </button>
            )}
          />
        )}
      </div>

      <PayloadModal
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
        onViewMandate={(entry) => {
          setSelectedEntry(null);
          const targetRef = entry.refNo?.startsWith("EM") || entry.refNo?.startsWith("REF-")
            ? entry.refNo
            : entry.payload?.refNo || entry.payload?.mandateId || entry.refNo;
          if (targetRef) {
            navigate(`/admin/mandates/${targetRef}`);
          }
        }}
      />
    </div>
  );
}